/**
 * Created by Abner Sui on 12/05/2019
 * -------------------------------------
 * Updated by Daniel Wang on 3/18/2021 - Added export feature
 * 10/20/2021 Simon Zhao notified subscribers of kanban refresh subject to refresh each time the workflow definition is changed.
 * Updated by Daniel Wang on 07/01/2022 - Refactor this component to support loading it on homescreen.
 */

import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, of, Subject, timer } from 'rxjs';
import { ProcessDefinition, ProcessInstance, Task } from '../../../tamalelibs/models/workflow.model';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../redux';
import { workflowMapSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { filter, catchError, take, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { GetOneProcessDefinition, WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { SystemUser } from '../../../tamalelibs/models/user.model';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { WorkflowTaskService } from '../../../tamalelibs/services/workflow-task.service';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { WorkflowKanbanScreenStateService } from '../../../components/work-flow/workflow-kanban-screen-state/workflow-kanban-screen-state.service';
import { MessageService } from './../../../services/message.service';
import { ProcessDefinitionViewModel } from '../../../components/work-flow/process-definition-view.model';
import { HomeScreenService } from '../../../services/home-screen.service';
import { businessConstants } from '../../../constants/business.constants';
import { AuthHelperService } from '../../../services/auth-helper.service';

@Component({
    selector: 'tam-workflow-kanban-screen',
    templateUrl: './workflow-kanban-screen.component.html',
    styleUrls: ['./workflow-kanban-screen.component.scss'],
    providers: [WorkflowKanbanScreenStateService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowKanbanScreenComponent implements OnInit, OnDestroy {
    @Input() actionSubject$?: Subject<any> = new Subject(); // event should be triger when change workflow tab or add workflow tab
    @Input() firstPageTabId?: string;
    @Input() id: string;
    @Input() instanceId?: string;
    @Input() isShowAsTab = false;
    @Input() loadingSpecificScreen?: boolean; // don't load when refreshing homescreen
    @Input() pageId?: string;
    @Input() screenId: string; // workflow screen id
    @Input() taskId?: string;

    //#region Public properties
    contentsHeight = '100%';
    currentUser: SystemUser;
    disableStartNewProject = false;
    isAdminUser = false;
    isIPAD = false;
    isLoading = true;
    isPPCUser = false;
    operationContextMenuConfig: ContextMenuConfig;
    selectedIndex = 0;
    /**
     * an array contains the click count for each tab
     *
     * @memberof WorkflowKanbanScreenComponent
     */
    tabClickStats = [0, 0];

    /**
     * a flag indicating whether the task kanban is activated.
     *
     * @readonly
     * @memberof WorkflowKanbanScreenComponent
     */
    get isTaskKanbanActive() {
        return this.selectedIndex === 0;
    }
    /**
     * a flag indicating whether the instance kanban is activated.
     *
     * @readonly
     * @memberof WorkflowKanbanScreenComponent
     */
    get isInstanceKanbanActive() {
        return this.selectedIndex === 1;
    }
    /**
     * a flag indicating whether the instance board tab has been clicked once.
     *
     * @readonly
     * @memberof WorkflowKanbanScreenComponent
     */
    get isInstanceKanbanClicked() {
        return this.tabClickStats[1] > 0;
    }
    /**
     * the wrapper of process definition passed to view components.
     *
     * passing this instead of definition model, as the update of the definition model would not trigger the change detection on view components.
     *
     * @type {ProcessDefinitionViewModel}
     * @memberof WorkflowKanbanScreenComponent
     */
    selectedProcessDefVm: ProcessDefinitionViewModel = new ProcessDefinitionViewModel();
    get selectedProcessDef() {
        return this.selectedProcessDefVm && this.selectedProcessDefVm.definition && this.selectedProcessDefVm.definition;
    }

    set selectedProcessDef(v: ProcessDefinition) {
        this.selectedProcessDefVm.definition = ProcessDefinition.filterOutGateways(v);
    }
    //#endregion

    //#region  Private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _isFirstLoad = false;
    private _processDefinitionMap: Map<string, ProcessDefinition>;
    private _reloadSubject$: Subject<string> = new Subject();
    //#endregion

    //#region constructor
    constructor(
        private _authHelper: AuthHelperService,
        private _detectChange: ChangeDetectorRef,
        private _deviceService: DeviceDetectorService,
        private _homeScreenService: HomeScreenService,
        private _kanbanStateService: WorkflowKanbanScreenStateService,
        private _messageService: MessageService,
        private _router: Router,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _taskService: WorkflowTaskService,
        private _toast: ToastService,
        private _workflowService: WorkflowService,
    ) { }
    //#endregion

    //#region  Public functions
    ngOnInit() {
        // Need to set the content height in different cases to avoid overwriting the navigation buttons.
        if (!this.isShowAsTab) {
            // the content height in the workflow page.
            this.contentsHeight = 'calc(100% - 91px)';
        } else {
            // the content height in the homescreen.
            this.contentsHeight = 'calc(100% - 31px)';
        }
        this._isFirstLoad = true;
        // initialize the value for variables
        this.currentUser = this._storeQuerier.getCurrentUser();

        this.isAdminUser = this.currentUser && this.currentUser.credential && this.currentUser.credential.admin;
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
        this.isPPCUser = this.currentUser && this.currentUser.credential &&
            this.currentUser.credential.publishPubConfiguration;
        this._initHandlers();
        // initialize context menu
        this._initOperateContextMenuConfig();
        if (!this.loadingSpecificScreen && this.firstPageTabId === this.pageId) {
            this._loadWorkflowKanbanView();
        }
        this._isFirstLoad = false;
        this._detectChange.detectChanges();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
        // remove process definition from store
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_ONE_PROCESS_DEFINITION,
            payload: this.id
        });
        this.id = null;
        this._processDefinitionMap = null;
        this._clearKanbanFilter();
    }

    onOperationMenuSelect(event) {
        const type = event.payload.id;
        switch (type) {
            case '0':
                this._editWorkflow();
                break;
            case '1':
                this._exportWorkflow();
                break;
            default:
                break;
        }
    }

    onOperationShowContextMenu(e: MouseEvent) {
        this.operationContextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    onTabSelectChange(index: number): void {
        if (this.selectedIndex !== index) {
            this.selectedIndex = index;
            this.tabClickStats[this.selectedIndex]++;
            this._detectChange.detectChanges();
        }
    }

    startNewProcess(event): void {
        this.disableStartNewProject = true;
        this._workflowService.startNewProcessInstance(this.selectedProcessDef.id).pipe(
            catchError(err => {
                return of(null);
            }),
            take(1),
        ).subscribe(res => {
            if (res) {
                const instance: ProcessInstance = ProcessInstance.parse(res, this.selectedProcessDef.id, this.selectedProcessDef.template);
                if (instance.task[0].assignee.some(ass => ass.id === this.currentUser.id)) {
                    this._slideSheetService.showSlideSheetForTask(null, instance.task[0], instance, this.selectedProcessDef, this._taskService.getTaskEditable(instance.task[0], this.currentUser), true);
                }

                if (this.isTaskKanbanActive) {
                    this._kanbanStateService.selectedTaskId = instance && instance.task.length > 0 ? instance.task[0].id : undefined;
                }

                if (this.isInstanceKanbanActive) {
                    this._kanbanStateService.selectedInstanceId = instance.id;
                }

                this.disableStartNewProject = false;
                this._messageService.dispatchProcessInstanceAction(instance);
            } else {
                this.disableStartNewProject = false;
                this._toast.notify({
                    style: NotificationStyles.Error,
                    message: `Process '${this.selectedProcessDef.name}' fails to start one new instance.`,
                });
            }
            this._detectChange.detectChanges();
        });
    }
    //#endregion

    //#region Private functions
    private _clearKanbanFilter() {
        this._store.dispatch({
            type: WorkflowActionTypes.CLEAR_WORKFLOW_KANBAN_FILTER,
        });
    }

    private _editWorkflow() {
        this._router.navigate(['workflow/config/edit', this.screenId]);
    }

    private _exportWorkflow() {
        // get export workflow data from server
        this._workflowService.getExportWorkflowJsonById(this.id).pipe(
            catchError(() => {
                return of('error');
            }),
            take(1)
        ).subscribe((res) => {
            if (res && res !== 'error') {
                const jsonFileName = this.selectedProcessDef.name + ' - configuration.json';
                const mockLink: HTMLAnchorElement = document.createElement('a'); // create a mock <a> for save file
                mockLink.download = jsonFileName; // the file name
                const fileContent: string = JSON.stringify(res);
                mockLink.href = 'data:text/plain;charset=utf-16,' + encodeURIComponent(fileContent);
                mockLink.click();  // this will trigger the dialog window
                mockLink.remove();
            } else {
                const msg = 'Export workflow ' + this.selectedProcessDef.name + ' failed.';
                this._toast.notify({
                    style: NotificationStyles.Error,
                    message: msg
                });
            }
            this._detectChange.detectChanges();
        });
    }

    private _initOperateContextMenuConfig() {
        this.operationContextMenuConfig = new ContextMenuConfig();
        this.operationContextMenuConfig.stateful = false;
        this.operationContextMenuConfig.showItemIcon = false;
        this.operationContextMenuConfig.clickOnDiv = false;
        this.operationContextMenuConfig.items = [];

        if (!this.isIPAD) {
            if (this.isPPCUser) {
                this.operationContextMenuConfig.items.push({ id: '0', text: 'Edit Workflow' });
            }
            if (this.isAdminUser) {
                this.operationContextMenuConfig.items.push({ id: '1', text: 'Export Workflow Configuration' });
            }
        }

        this.operationContextMenuConfig.events$.subscribe(event => this.onOperationMenuSelect(event));
    }

    private _initHandlers() {
        this._kanbanStateService.screenId = this.screenId;
        this._destroySubscriptions.push(
            this.actionSubject$.subscribe(event => this._onSubscribeActionsEvent(event)),
            // subscribe the refresh subject.
            this._reloadSubject$.pipe(
                debounceTime(0),
                filter(() => !!this.id && !!this._processDefinitionMap),
            ).subscribe(() => {
                const token = this._authHelper.getToken();
                if (token) {
                    this._loadWorkflowKanbanView();
                }
            }),

            // query all workflow definitions.
            this._store.pipe(
                select(workflowMapSelector),
                filter(item => this.isLoading || (!this.isLoading && item && item.size)),
            ).subscribe((itemMap: Map<string, ProcessDefinition>) => {
                this.isLoading = true;
                this._processDefinitionMap = itemMap;
                if (this.isShowAsTab) {
                    if (!this._isFirstLoad) {
                        this._reloadSubject$.next('');
                    }
                } else {
                    this._reloadSubject$.next('');
                }
            }),

            // handle the case where failing to obtain the process definitions.
            this._store.pipe(
                select(lastCustomActionSelector),
                filter((action) => action.type === WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION_FAILED),
            ).subscribe(() => {
                this._router.navigate(['error']);
            })
        );
    }

    private _loadWorkflowKanbanView() {
        // receive a refresh request
        // 1. get the selected process definition
        this.selectedProcessDef = this._processDefinitionMap.get(this.id);
        if (!this.selectedProcessDef) {
            // 2.1 refresh rather than initialization, dispatch action to retrive it.
            this._store.dispatch(new GetOneProcessDefinition({ id: this.id }));
        } else {
            // 2.2 found the definition
            this.isLoading = false;
            // 2.2.1 determine whether there's a trigger condition setting for the selected process
            const triggerCondition = this.selectedProcessDef.configuration.triggerCondition;
            if (triggerCondition) {
                // 2.2.2.1 disable the new-project button if the trigger method is not manual.
                this.disableStartNewProject = !triggerCondition.triggerMethod.manual;
            }
            if (this.taskId && this.instanceId) {
                // 2.2.2.2 find the instance data by the instance id if both the instance id and the task id are found.
                const ins: ProcessInstance = this.selectedProcessDef.processInstance.find(item => item.id === this.instanceId);
                if (ins) {
                    // 2.2.2.2.1 continue to find the task data by the task id if the instance data is found.
                    const tempTask: Task = ins.task.find(item => item.id === this.taskId);
                    if (tempTask) {
                        // 2.2.2.2.1.1 slide out the sidesheet and fill with the data of the found task.
                        this._slideSheetService.showSlideSheetForTask(null, tempTask, ins, this.selectedProcessDef, this._taskService.getTaskEditable(tempTask, this.currentUser));
                    }
                }
                this.taskId = null;
                this.instanceId = null;
            }
            this._homeScreenService.reloadingAssignedTab$.next({ type: businessConstants.screenType.workflow });
        }

        this._kanbanStateService.refreshSubject$.next();
        this._detectChange.detectChanges();
    }

    private _onSubscribeActionsEvent(event) {
        // tigger init components when adding/changing workflow tab on homescreen
        if (event.type === 'REFRESH_WORKFLOW') {
            this.id = event.payload.workflowId;
            this.screenId = event.payload.screenId;
            this._kanbanStateService.screenId = this.screenId;
            // 1. remove the previous workflow from the store.
            // The previous Workflow needs to be removed from the store when switching between workflows
            this._store.dispatch({
                type: WorkflowActionTypes.CLEAR_ONE_PROCESS_DEFINITION,
                payload: this.id
            });
            this._processDefinitionMap.delete(this.id);
            // 2. notify the refresh subject.
            this._reloadSubject$.next('');

        }
    }
    //#endregion
}
